<template>
    <input
        ref="inputRef"
        style="display: none"
        type="file"
        :accept="effectiveAccept"
        :capture="capture"
        :multiple="multiple"
        @change="updateFiles"
    />
    <slot
        :sign="sign"
        :store="store"
        :upload="upload"
        :openDialog="openDialog"
        :uploadProgess="uploadProgress"
        :fileProgress="fileProgress"
        :fileStatus="fileStatus"
        :status="{
            hasSigned: hasSigned,
            hasStored: hasStored,
            hasUploaded: hasUploaded,
            isUploading: isUploading,
            isSigning: isSigning,
            isStoring: isStoring,
            hasError: hasError,
        }"
        :statusText="statusText"
        :reset="reset"
    />
</template>
<script setup>
const props = defineProps({
    modelValue: {
        type: Array,
        required: true,
    },
    url: {
        type: String,
        required: true,
    },
    type: {
        type: String,
        required: true,
    },
    multiple: {
        type: Boolean,
        required: false,
        default: false,
    },
    scope: {
        type: String,
        required: false,
        default: 'tenant',
    },
    isSigning: {
        type: Boolean,
        required: false,
        default: false,
    },
    isStoring: {
        type: Boolean,
        required: false,
        default: false,
    },
    useBatchUpload: {
        type: Boolean,
        required: false,
        default: false,
    },
    accept: {
        type: String,
        required: false,
        default: '*/*',
    },
    capture: {
        type: String,
        required: false,
        default: null,
    },
    androidAllowCamera: {
        type: Boolean,
        required: false,
        default: false,
    },
});

const effectiveAccept = computed(() => {
    if (!props.androidAllowCamera || !props.accept) {
        return props.accept;
    }

    const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    if (!/android/i.test(userAgent)) {
        return props.accept;
    }

    const tokens = props.accept
        .split(',')
        .map((token) => token.trim())
        .filter(Boolean);

    const hasMediaAccept = tokens.some((token) => /^(image|video)\//i.test(token));
    const hasAndroidAllowCamera = tokens.some((token) => token.toLowerCase() === 'android/allowcamera');

    if (!hasMediaAccept || hasAndroidAllowCamera) {
        return props.accept;
    }

    return [...tokens, 'android/allowCamera'].join(',');
});

const axios = computed(() => {
    if (props.scope === 'tenant') {
        return window.tenantAxios;
    } else {
        return window.centralAxios;
    }
});

// const emit = defineEmits(['update:modelValue']);
const emit = defineEmits(['update:modelValue', 'uploaded']);

// Upload dialog
const inputRef = ref();
const openDialog = () => {
    inputRef.value.click();
};

const updateFiles = () => {
    emit('update:modelValue', [...Array.from(props.modelValue), ...Array.from(inputRef.value.files)]);
};

provide('addFiles', (files) => {
    emit('update:modelValue', [...Array.from(props.modelValue), ...Array.from(files)]);
});

const allFiles = ref();
const statusText = ref();
const hasSigned = ref(false);
const hasStored = ref(false);
const hasUploaded = ref(false);
const isUploading = ref(false);
const hasError = ref(false);

const uploadProgress = ref(0);

// Per-file upload state, index-aligned with the signed responses / allFiles.
// Additive alongside the shared `uploadProgress` scalar so existing consumers
// keep working; consumers that want per-file bars read these instead.
const fileProgress = ref([]);
const fileStatus = ref([]);

async function sign() {
    isUploading.value = true;
    hasSigned.value = false;
    hasStored.value = false;
    hasUploaded.value = false;
    statusText.value = 'Signing...';
    const files = Array.from(props.modelValue);
    if (files.length) {
        allFiles.value = files;
        let promises = [];

        files.forEach((el) => {
            const promise = (async () => {
                const resp = await axios.value.post(`${props.url}/vapor/signed-storage-url`, {
                    bucket: import.meta.env.VITE_BUCKET_UPLOAD,
                    content_type: el.type,
                    expires: '',
                    visibility: '',
                    // Original filename — presign endpoints that key by name (e.g.
                    // migration source uploads) use it; others ignore it.
                    name: el.name,
                });
                return resp.data;
            })();
            promises.push(promise);
        });
        let returnItem = [];
        const results = await Promise.allSettled(promises);
        const fulfilled = results.filter((result) => result.status === 'fulfilled').map((result) => result.value);

        if (fulfilled.length === promises.length) {
            fulfilled.forEach((el) => {
                returnItem.push(el);
            });
            console.log('Signed', returnItem);
            return returnItem;
        }

        const rejected = results.filter((result) => result.status === 'rejected').map((result) => result.reason);

        if (rejected.length) {
            isUploading.value = false;
            uploadProgress.value = 0;
            statusText.value = 'Failure!';
            hasError.value = true;
            rejected.forEach((error) => {
                console.log('Store failure', error);
            });
            throw Error('Signing :: Upload failure');
        }
    }
}

async function store(signedUrlResponses) {
    statusText.value = 'Storing...';
    fileProgress.value = signedUrlResponses.map(() => 0);
    fileStatus.value = signedUrlResponses.map(() => 'uploading');
    let promises = [];
    signedUrlResponses.forEach((signedUrlResponse, idx) => {
        console.log('Response store is using:', signedUrlResponse);
        console.log('File Value:', allFiles.value[idx]);
        const BASE_API_URL = import.meta.env.VITE_API_BASE_URL;
        const axiosInstance = axios.value.create({
            baseURL: BASE_API_URL,
            headers: {
                'X-Request-With': 'XMLHttpRequest',
                'Content-Type': allFiles.value[idx].type,
            },
            // withCredentials: true,
        });

        const promise = axiosInstance.put(signedUrlResponse.url, allFiles.value[idx], {
            onUploadProgress: (progressEvent) => {
                const percent = progressEvent.total ? (progressEvent.loaded / progressEvent.total) * 100 : 0;
                uploadProgress.value = percent;
                fileProgress.value[idx] = percent;
            },
        });
        promises.push(promise);
    });

    // Storing
    console.log('Storing...');

    const results = await Promise.allSettled(promises);

    console.log('Results of storing', results);

    results.forEach((result, idx) => {
        fileStatus.value[idx] = result.status === 'fulfilled' ? 'stored' : 'failed';
        if (result.status === 'fulfilled') {
            fileProgress.value[idx] = 100;
        }
    });

    const fulfilled = results.filter((result) => result.status === 'fulfilled').map((result) => result.value);

    if (fulfilled.length === promises.length) {
        hasStored.value = true;
        statusText.value = 'Stored!';
        console.log('Storing success:', fulfilled);
        return fulfilled;
    }

    const rejected = results.filter((result) => result.status === 'rejected').map((result) => result.reason);

    if (rejected.length) {
        isUploading.value = false;
        uploadProgress.value = 0;
        hasError.value = true;
        statusText.value = 'Failure!';
        rejected.forEach((error) => {
            console.log('Store failure', error);
        });
        throw Error('Store :: Upload failure');
    }
}

async function upload(signResponses, additionalParams = {}) {
    console.log('Upload called');
    statusText.value = 'Uploading...';

    // Try batch upload first if multiple files and batch upload is enabled
    if (signResponses.length > 1 && props.useBatchUpload) {
        try {
            const files = signResponses.map((signResponse, index) => ({
                uuid: signResponse.uuid,
                key: signResponse.key,
                bucket: signResponse.bucket,
                name: allFiles.value[index].name,
                content_type: allFiles.value[index].type,
                size: Math.ceil(allFiles.value[index].size / 1024),
            }));

            const batchPayload = {
                files,
                ...additionalParams,
            };

            const batchResponse = await axios.value.post(`${props.url}/${props.type}/batch`, batchPayload);

            hasUploaded.value = true;
            isUploading.value = false;
            uploadProgress.value = 0;
            hasError.value = false;
            statusText.value = 'Uploaded!';

            console.log('[Uploader.vue] Batch upload complete, emitting uploaded');
            emit('uploaded');

            return [batchResponse];
        } catch (error) {
            // If batch endpoint doesn't exist (404), fall back to individual uploads
            if (error?.response?.status !== 404) {
                // Re-throw non-404 errors
                throw error;
            }
            console.log('[Uploader.vue] Batch endpoint not available, falling back to individual uploads');
        }
    }

    // Fall back to individual uploads (original logic)
    let promises = [];
    signResponses.forEach((signResponse, index) => {
        console.log('StoreResponse', signResponse);
        const promise = axios.value.post(`${props.url}/${props.type}`, {
            uuid: signResponse.uuid,
            key: signResponse.key,
            bucket: signResponse.bucket,
            name: allFiles.value[index].name,
            content_type: allFiles.value[index].type,
            size: Math.ceil(allFiles.value[index].size / 1024),
            ...additionalParams,
        });
        promises.push(promise);
    });
    const results = await Promise.allSettled(promises);

    const fulfilled = results.filter((result) => result.status === 'fulfilled').map((result) => result.value);

    if (fulfilled.length === promises.length) {
        hasUploaded.value = true;
        isUploading.value = false;
        uploadProgress.value = 0;
        hasError.value = false;
        statusText.value = 'Uploaded!';

        console.log('[Uploader.vue] Upload complete, emitting uploaded');
        emit('uploaded');

        return fulfilled;
    }

    const rejected = results.filter((result) => result.status === 'rejected').map((result) => result.reason);

    if (rejected.length) {
        hasError.value = true;
        isUploading.value = false;
        uploadProgress.value = 0;
        statusText.value = 'Failure!';
        let errors = {};
        rejected.forEach((error) => {
            if (error?.errors && typeof error.errors === 'object') {
                errors = { ...errors, ...error.errors };
            }
        });

        // Prefer the first rejection for message/response context
        const first = rejected[0];
        let data = first?.response?.data || first?.data;
        if (typeof data === 'string') {
            try {
                data = JSON.parse(data);
            } catch {
                // keep as string
            }
        }
        // Merge backend field errors
        if (data?.errors && typeof data.errors === 'object') {
            errors = { ...errors, ...data.errors };
        }
        const message =
            (data && data.message) || first?.message || (data && data.error) || first?.error || 'Upload failed.';
        const status = first?.response?.status ?? first?.status;

        const enrichedError = new Error(message);
        enrichedError.errors = errors;
        enrichedError.response = first?.response;
        enrichedError.status = status;
        enrichedError.data = data;
        enrichedError.original = first;

        throw enrichedError;
    }
}

function reset() {
    hasError.value = false;
    statusText.value = null;
    hasSigned.value = false;
    hasStored.value = false;
    hasUploaded.value = false;
    isUploading.value = false;
    uploadProgress.value = 0;
    fileProgress.value = [];
    fileStatus.value = [];
    allFiles.value = null;
    inputRef.value.value = '';
    emit('update:modelValue', []);
}

defineExpose({
    sign,
    store,
    upload,
    uploadProgress,
    fileProgress,
    fileStatus,
});
</script>
